/**
 * API client for the AI document split + classify + entity-match endpoints.
 *
 * Backend: Lender.Optimus.Ai #682 — AiDocumentBoundarySplitController
 *
 * Split:    POST document-boundary-splits/detect
 *           multipart/form-data — field: file (combined PDF, ≤50 MB)
 *           Response: { totalPages, boundaries, segments: [{ startPageIndex, endPageIndex }], truncated }
 *
 * Classify: POST document-segment-classifications
 *           { firstPageBase64, firstPageMediaType, additionalPageImages?, candidateDocumentTypeIds? }
 *           Response: { documentTypeId, confidence }
 *
 * Entity match (DEV-4189): POST document-entity-matches
 *           { loanApplicationId, extractedFields, documentTypeId }
 *           Response: { borrowers[], incomes[], assets[], liabilities[], properties[] }
 *           Each item: { entityType, entityId, displayName, matchScore, matchRationale }
 */
export class AiDocumentSplitApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  /**
   * Detect document boundaries in a combined PDF.
   * @param {{ file: File }} params
   */
  async splitDocument({ file }) {
    const formData = new FormData();

    formData.append('file', file);

    return this.#http.post('document-boundary-splits/detect', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  /**
   * Classify a single document segment by its first page image.
   * @param {{
   *   firstPageBase64: string,
   *   firstPageMediaType: string,
   *   additionalPageImages?: string[],
   *   candidateDocumentTypeIds?: string[],
   * }} params
   */
  async classifySegment(params) {
    return this.#http.post('document-segment-classifications', params);
  }

  /**
   * Propose entity matches from extracted fields.
   * @param {{
   *   loanApplicationId: string,
   *   extractedFields: Array<{ fieldName: string, fieldValue: string|null }>,
   *   documentTypeId: string,
   * }} params
   * @returns {{ borrowers: object[], incomes: object[], assets: object[], liabilities: object[], properties: object[] }}
   */
  async proposeEntityMatches(params) {
    return this.#http.post('document-entity-matches', params);
  }
}
